import { useMemo, useState, Suspense } from 'react'
import { Group, Loader, Select, Stack, Table, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconChevronDown } from '@tabler/icons-react'
import dayjs from 'dayjs'
import clsx from 'clsx'
import Meta from '../common/Meta'
import Header from '../common/components/header/Header'
import Tabs from '../stories/Tabs/Tabs'
import { useLoxoKpis } from './api'
import LoxoScoringSimulation from './LoxoScoringSimulation'
import classes from './LoxoKpis.module.css'

const TABS = [
  { value: 'raw', label: 'Raw Data' },
  { value: 'scoring', label: 'Scoring Simulation' },
]

export default function LoxoKpis() {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(2025, 0, 1),
    new Date(2026, 0, 1),
  ])
  const [activeTab, setActiveTab] = useState('raw')

  return (
    <div className={classes.container}>
      <Meta title="Loxo KPIs" />
      <Header title="Loxo KPIs" />
      <div
        className={clsx(classes.wrapper, {
          [classes.wrapperMobile]: !isDesktop,
        })}
      >
        <Tabs items={TABS} value={activeTab} onChange={setActiveTab} />
        <div className={classes.controlsBar}>
          <Group gap="sm" wrap="wrap">
            <DatePickerInput
              classNames={{ input: classes.input }}
              clearable
              type="range"
              placeholder="Date range"
              leftSection={<IconCalendar className={classes.filterIcon} />}
              rightSection={<IconChevronDown className={classes.filterIcon} />}
              leftSectionPointerEvents="none"
              rightSectionPointerEvents="none"
              value={dateRange}
              onChange={(value) =>
                setDateRange(value as [Date | null, Date | null])
              }
              popoverProps={{ withinPortal: true, zIndex: 400 }}
            />
          </Group>
        </div>
        <Suspense
          fallback={
            <Stack w="100%" h="400px" justify="center" align="center">
              <Loader color="#0B6B7B" />
            </Stack>
          }
        >
          {activeTab === 'raw' ? (
            <LoxoKpisContent dateRange={dateRange} />
          ) : (
            <LoxoScoringSimulation dateRange={dateRange} />
          )}
        </Suspense>
      </div>
    </div>
  )
}

function LoxoKpisContent({
  dateRange,
}: {
  dateRange: [Date | null, Date | null]
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const start = dayjs(dateRange[0] ?? '2025-01-01')
    .startOf('month')
    .format('YYYY-MM-DD')
  const end = dayjs(dateRange[1] ?? '2026-01-01')
    .startOf('month')
    .format('YYYY-MM-DD')

  const { data } = useLoxoKpis({ start, end })

  const kpiNames = useMemo(
    () => Array.from(new Set(data.map((r) => r.kpi))).sort(),
    [data]
  )

  const [selectedKpi, setSelectedKpi] = useState<string | null>(null)
  const activeKpi = selectedKpi ?? kpiNames[0] ?? null

  const months = useMemo(() => {
    const result: string[] = []
    let cursor = dayjs(start)
    const endDate = dayjs(end)
    while (!cursor.isAfter(endDate)) {
      result.push(cursor.format('YYYY-MM-DD'))
      cursor = cursor.add(1, 'month')
    }
    return result
  }, [start, end])

  type PivotRow = {
    user_id: number
    name: string
    total: number
    values: Record<string, number>
  }

  const pivotRows = useMemo(() => {
    const filtered = data.filter((r) => r.kpi === activeKpi)
    const byUser = new Map<number, PivotRow>()

    for (const row of filtered) {
      let entry = byUser.get(row.user_id)
      if (!entry) {
        entry = { user_id: row.user_id, name: row.name, total: 0, values: {} }
        byUser.set(row.user_id, entry)
      }
      const monthKey = dayjs(row.period).startOf('month').format('YYYY-MM-DD')
      entry.values[monthKey] = (entry.values[monthKey] ?? 0) + row.value
      entry.total += row.value
    }

    return Array.from(byUser.values()).sort((a, b) => b.total - a.total)
  }, [data, activeKpi])

  const totals = useMemo(() => {
    const monthTotals: Record<string, number> = {}
    let grandTotal = 0
    for (const row of pivotRows) {
      grandTotal += row.total
      for (const month of months) {
        monthTotals[month] =
          (monthTotals[month] ?? 0) + (row.values[month] ?? 0)
      }
    }
    return { monthTotals, grandTotal }
  }, [pivotRows, months])

  return (
    <>
      <div className={classes.controlsBar}>
        <Group gap="sm" wrap="wrap">
          <Select
            placeholder="Select KPI"
            data={kpiNames}
            value={activeKpi}
            onChange={setSelectedKpi}
            classNames={{ input: classes.input, dropdown: classes.dropdown }}
            style={{ minWidth: 200 }}
          />
        </Group>
      </div>

      <Text fw={500} size="lg">
        Breakdown by Month
      </Text>

      {pivotRows.length === 0 ? (
        <Text className={classes.empty}>
          No Loxo KPI data available for the selected date range.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={600}>
          <Table
            classNames={{
              table: clsx(classes.table, classes.stickyColumns),
              th: classes.th,
              td: classes.td,
              tr: classes.headerRow,
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: isDesktop ? 160 : 120 }}>
                  Name
                </Table.Th>
                <Table.Th>Total</Table.Th>
                {months.map((m) => (
                  <Table.Th key={m}>{dayjs(m).format('MMM YY')}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr className={classes.totalsRow}>
                <Table.Td>Total</Table.Td>
                <Table.Td style={{ fontWeight: 700 }}>
                  {totals.grandTotal.toLocaleString()}
                </Table.Td>
                {months.map((m) => (
                  <Table.Td key={m}>
                    {(totals.monthTotals[m] ?? 0).toLocaleString()}
                  </Table.Td>
                ))}
              </Table.Tr>
              {pivotRows.map((row) => (
                <Table.Tr key={row.user_id}>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td style={{ fontWeight: 600 }}>
                    {row.total.toLocaleString()}
                  </Table.Td>
                  {months.map((m) => (
                    <Table.Td key={m}>
                      {(row.values[m] ?? 0).toLocaleString()}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </>
  )
}
